import { Collapse } from "antd";
import { pipe, ReadonlyArray } from "fp";
import React from "react";
import styled from "styled-components";

import { useAction } from "~/Action";
import * as Button from "~/Button";
import * as Data from "~/Data";
import * as Document from "~/Document";
import cabinet from "~/Document/Cabinet.svg";
import * as Driver from "~/Driver";
import { Component as EmptyComponent } from "~/Empty";
import * as Table from "~/Table";
import * as TextContent from "~/TextContent";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

export const Component: React.Fn = () =>
  pipe(
    Document.useAllSorted(),
    ReadonlyArray.mapWithIndex((index, document) => (
      <Row.Component key={`${document.code}-${index}`} {...document} />
    )),
    (documents) => (
      <>
        <Wide.Component />
        <Table.Body.Component>{documents}</Table.Body.Component>
      </>
    )
  );

export namespace Wide {
  export const Component: Component = () =>
    pipe(
      Document.useStore(),
      Data.Store.when({
        loading: () => (
          <Table.Wide.Loading.Component colSpan={Document.Table.colSpan()} />
        ),
        error: (error) => (
          <Table.Wide.ErrorMessage.Component colSpan={Document.Table.colSpan()}>
            {error.message}
          </Table.Wide.ErrorMessage.Component>
        ),
        success: (cache) =>
          Data.Cache.isEmpty(cache) ? <Empty.Component /> : null,
      })
    );

  export namespace Empty {
    export const Component: Component = () => (
      <EmptyComponent
        title="You currently don't have any documents."
        subtitle="I'm going to need you to come in on Saturday."
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        svg={{ src: cabinet }}
      />
    );
  }
}

export namespace Row {
  export const Component: React.Fn<Document.Document> = React.memo((document) =>
    pipe(TextContent.useTextContent(), ({ ref, textContent }) => (
      <Container.Component
        // TODO: Properly type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-assignment
        ref={ref as any}
        hide={
          !Document.Search.matches(textContent, Document.Search.useSearch())
        }
        {...document}
      >
        <Column.Component style={{ width: "fit-content" }}>
          <Collapse ghost>
            <Collapse.Panel
              key={1}
              header={
                <Typography.Component thin>
                  {Time.DateTime.toDateOnlyMMMDYYYYIgnoreTimezone(document)}
                </Typography.Component>
              }
            >
              <Document.Detail.Thumbnail.Component document={document} />
            </Collapse.Panel>
          </Collapse>
        </Column.Component>
        <Column.Component>
          <Driver.Name.Component {...document.driver} />
        </Column.Component>
        <Column.Component>{document.type}</Column.Component>
        <Column.Component>{document.reference}</Column.Component>
        <Column.Component>
          <Button.Component
            title="View"
            onClick={useAction(Document.Detail.Action.Updated.create(document))}
          />
        </Column.Component>
      </Container.Component>
    ))
  );

  namespace Container {
    export const Component = styled(Table.Row.Component)<
      Document.Document & { readonly hide?: boolean }
    >`
      ${({ hide }) => (hide ? `display: none;` : "")}
    `;
  }
}

export namespace Column {
  type Props = {
    readonly width?: string;
    readonly center?: boolean;
  };

  export const Component = styled(Table.Column.Component)<Props>`
    width: ${({ width }) => width ?? "unset"};
    max-width: ${({ width }) => width ?? "unset"};
    text-align: ${({ center }) => (center ? "center" : "left")};
  `;
}
